import { useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useLocalStorage } from "../../../core/hooks/useLocalStorage"
import TagFunctions, { ITagsProp } from "../../../core/functions/TagFunctions"
import { TSaleStorage } from "../../../core/types/Sale"
import Hosts from "../../../core/functions/HostFunctions"
import React from "react"
import MetaFunctions from "../../../core/functions/MetaFunctions"
import CheckoutFunctions from "../../../core/functions/CheckoutFunctions"
import { useQuery } from "../../../components/Query/useQuery"
import GoogleFunctions from "../../../core/functions/GoogleFunctions"
import { useCheckoutContext } from "../../../core/hooks/useCheckoutContext"
import CardUpdatedSuccess from "./CardUpdatedSuccess"
import OrderSuccess from "./OrderSuccess"
import { paymentMethodImmediatePurchase } from "../../../core/constants/PaymentConsts"

const CheckoutSuccessPage = (): JSX.Element => {
  const { codeId } = useParams<{ codeId?: string }>()

  const query = useQuery()
  const { isFacebookPixelSent, addFacebookPixelSent, isPurposeCardUpdate } = useCheckoutContext()

  const redirect = query.get("redirect")
  const isRedirectedParam = redirect !== "no"

  const googleAds = useRef<ITagsProp[] | null>(null)
  const tagInitialDataLayer = useRef<ITagsProp | null>(null)
  const tagDataLayer = useRef<ITagsProp | null>(null)
  const tagGTM = useRef<ITagsProp | null>(null)

  const [saleStorage] = useLocalStorage("afpa:sale", null)

  const [sale, setSale] = useState<TSaleStorage | undefined>(undefined)
  const [redirectSeconds, setRedirectSeconds] = useState(3)
  const [isRedirect, setIsRedirect] = useState(false)

  const isRequestRedirect =
    isRedirectedParam && sale?.redirectUrl && sale.redirectUrl !== ""

  const navigate = useNavigate()

  useEffect(() => {
    if (saleStorage === null && !isPurposeCardUpdate()) {
      Hosts.openNavigate(`/${codeId}`, navigate)
      return
    }
    setSale(saleStorage)

    if (!tagInitialDataLayer.current) {
      tagInitialDataLayer.current = GoogleFunctions.initialDataLayer()
    }
    return () => {
      if (tagInitialDataLayer.current) {
        TagFunctions.removeTags(tagInitialDataLayer.current)
      }
    }
  }, [])

  useEffect(() => {
    if (sale && !isPurposeCardUpdate()) {
      if (!isFacebookPixelSent(sale.orderCode, "purchase")) {
        const hasPixels = MetaFunctions.hasPurchasePixels(sale, true)
        MetaFunctions.addTagPurchase(sale, true)
        MetaFunctions.sendPurchaseAPI(sale, true)
        if (hasPixels) {
          addFacebookPixelSent(sale.orderCode, "purchase")
        }
      }

      if (paymentMethodImmediatePurchase.includes(sale.paymentType)) {
        if (!googleAds.current) {
          googleAds.current = GoogleFunctions.addTagGoogleAdsPixelPurchase(sale)
        }

        if (!tagDataLayer.current) {
          tagDataLayer.current = GoogleFunctions.addDataLayerPurchase(sale)
        }
        if (!tagGTM.current) {
          tagGTM.current = GoogleFunctions.addGoogleTagManager(
            sale.googleTagManagerId
          )
        }
      }
    }
    return () => {
      if (tagDataLayer.current) {
        TagFunctions.removeTags(tagDataLayer.current)
      }
      if (tagGTM.current) {
        TagFunctions.removeTags(tagGTM.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale])

  useEffect(() => {
    if (sale && !isPurposeCardUpdate()) {
      if (isRedirect) {
        if (redirectSeconds === 0 && sale.redirectUrl) {
          const url = CheckoutFunctions.getUrlQueryThankyouPage(
            sale.redirectUrl,
            sale
          )
          window.location.href = Hosts.getUrlRedirect(url)
          return
        }

        setTimeout(() => {
          setRedirectSeconds((redirectSeconds) => redirectSeconds - 1)
        }, 1000)
      }

      if (isRequestRedirect) {
        setIsRedirect(true)
      }
    }
  }, [isRedirect, redirectSeconds, sale, isRequestRedirect])

  if (isPurposeCardUpdate()) {
    return <CardUpdatedSuccess sale={sale} />
  }

  return (
    <OrderSuccess
      sale={sale}
      isRedirect={isRedirect}
      redirectSeconds={redirectSeconds}
    />
  )
}

export default CheckoutSuccessPage
